// CHART-AGENTS (founder-approved): the agents (AG) on the chart. `GET org-chart` returns each
// shown holder's agents (a DE's or a person's); a seat card carries an "N agents" chip next to
// its holder line that opens them under the card, each an AG with a link to its Workforce page.
// Collapsed by default; which seats are open is remembered per company in this browser, as the
// chart remembers its view and dimensions.
import { cn, WorkerBadge, workerName } from "@companyos/ui";
import { Link } from "@tanstack/react-router";
import { ChevronDownIcon } from "lucide-react";
import { useState } from "react";
import { keepFocus } from "../../focus/focus";
import type { ChartSeat, OrgChart } from "./chart-model";

export type ChartAgent = OrgChart["agents"][number];

/** Each seat's agents: those of all its holders, in the API's order (by holder, then name). */
export function agentsBySeat(
  chart: Pick<OrgChart, "seats" | "agents"> | undefined,
): Map<string, ChartAgent[]> {
  const out = new Map<string, ChartAgent[]>();
  if (!chart) return out;
  const byHolder = new Map<string, ChartAgent[]>();
  // HELPERS-2: under its home holder and under every shown DE that uses it.
  for (const a of chart.agents ?? [])
    for (const lead of [a.reports_to_membership_id, ...(a.used_by_membership_ids ?? [])]) {
      const list = byHolder.get(lead) ?? [];
      list.push(a);
      byHolder.set(lead, list);
    }
  for (const s of chart.seats) {
    const list = [...new Set(s.holders.flatMap((h) => byHolder.get(h.membership_id) ?? []))];
    if (list.length) out.set(s.id, list);
  }
  return out;
}

// The viewer's open seats, per company, in this browser ------------------------------------------

const OPEN_KEY = (companyId: string) => `companyos.chart.agents.${companyId}`;

export function readOpenAgents(companyId: string): Set<string> {
  try {
    const raw = window.localStorage.getItem(OPEN_KEY(companyId));
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return new Set(
      Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : [],
    );
  } catch {
    return new Set();
  }
}

export function writeOpenAgents(companyId: string, open: ReadonlySet<string>): void {
  try {
    if (open.size === 0) window.localStorage.removeItem(OPEN_KEY(companyId));
    else window.localStorage.setItem(OPEN_KEY(companyId), JSON.stringify([...open]));
  } catch {}
}

/**
 * The open seats of `companyId` and a toggle. Codex chart-howiwork r1: the set belongs to one
 * company — when the chart stays mounted across a company switch, the other company's set is
 * read, and one company's seats are never written into another's.
 */
export function useOpenAgents(companyId: string): [ReadonlySet<string>, (seatId: string) => void] {
  const [state, setState] = useState(() => ({ companyId, open: readOpenAgents(companyId) }));
  const open = state.companyId === companyId ? state.open : readOpenAgents(companyId);
  // Codex r2: the state update stays pure (StrictMode may replay it); storage is written here.
  const toggle = (seatId: string) => {
    const next = new Set(open);
    if (next.has(seatId)) next.delete(seatId);
    else next.add(seatId);
    writeOpenAgents(companyId, next);
    setState({ companyId, open: next });
  };
  return [open, toggle];
}

/** "2 agents" on the card's holder line; toggles the list under it. */
export function SeatAgentsChip({
  seat,
  count,
  open,
  onToggle,
}: {
  seat: Pick<ChartSeat, "id" | "title">;
  count: number;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      data-slot="seat-agents-chip"
      aria-expanded={open}
      aria-controls={`seat-agents-${seat.id}`}
      aria-label={`${count} ${count === 1 ? "helper" : "helpers"} under ${seat.title}`}
      title={open ? "Hide helpers" : "Show helpers"}
      onClick={onToggle}
      className="ml-auto inline-flex shrink-0 items-center gap-0.5 rounded-full border border-subtle px-1.5 text-2xs leading-4 text-muted-foreground hover:border-strong hover:text-foreground"
    >
      {/* HELPERS: the one noun for the agents (AG) behind a seat's holders. */}
      {count} {count === 1 ? "helper" : "helpers"}
      <ChevronDownIcon
        aria-hidden="true"
        className={cn(
          "size-3 transition-transform duration-(--dur-fast)",
          open ? "rotate-180" : "",
        )}
      />
    </button>
  );
}

/** The open list: each agent with the AG badge and a link to its Workforce page. */
export function SeatAgentsList({
  seatId,
  agents,
  companySlug,
}: {
  seatId: string;
  agents: readonly ChartAgent[];
  companySlug: string;
}) {
  return (
    <ul
      id={`seat-agents-${seatId}`}
      data-slot="seat-agents"
      className="flex min-w-0 flex-col gap-0.5 border-t border-subtle pt-1"
    >
      {agents.map((a) => (
        <li key={a.id} data-ag={a.id} className="flex min-w-0 items-center gap-1">
          <WorkerBadge kind="ag" size="sm" />
          <Link
            to="/c/$slug/agents/ag/$id"
            params={{ slug: companySlug, id: a.id }}
            search={keepFocus}
            className="min-w-0 truncate text-2xs leading-4 text-foreground hover:underline"
            title={workerName(a.name)}
          >
            {workerName(a.name)}
          </Link>
          {a.status === "paused" || a.status === "not_deployed" ? (
            <span className="shrink-0 text-2xs leading-4 text-faint">
              {a.status === "paused" ? "Paused" : "Not deployed"}
            </span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
